import type Hls from 'hls.js'

export type StreamSource = {
  /** Progressive MP4: every video has one. */
  src: string
  /** HLS master playlist of videos processed by the media pipeline. */
  hls?: string | null
}

const HLS_MIME = 'application/vnd.apple.mpegurl'

/**
 * Plays a video over adaptive HLS where possible and falls back to its MP4.
 * Browsers with native HLS (Safari, iOS, recent Chrome) get the playlist as
 * `src`; the others load hls.js on first use. Videos without a stream, browsers
 * without MSE and streams that fail play the MP4, so a broken rendition never
 * leaves the viewer with a black screen.
 *
 * `load` gates segment downloads through hls.js so preloaded neighbours only
 * fetch the playlist, like `preload="metadata"` does for an MP4.
 *
 * `isBuffering` is true while the element has no frame to show or playback
 * stalls: videos without a poster would otherwise sit on a bare black box.
 */
export function useVideoStream(
  element: Readonly<Ref<HTMLVideoElement | null>>,
  source: MaybeRefOrGetter<StreamSource | null>,
  load: MaybeRefOrGetter<boolean>,
) {
  /** Streams that failed in this player: their MP4 plays instead. */
  const failedStreams = reactive(new Set<string>())
  let hls: Hls | null = null
  let loading = false
  /** Settles once the element has its source: `play()` before that would be aborted by hls.js attaching. */
  let attaching: Promise<void> = Promise.resolve()
  const isBuffering = ref(true)

  watch(element, (video, _previous, onCleanup) => {
    isBuffering.value = video === null || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA
    if (video === null) return
    const buffering = () => { isBuffering.value = true }
    const ready = () => { isBuffering.value = false }
    // A failed stream starts over with `loadstart` of its MP4; a broken video stops the spinner.
    const listeners = { loadstart: buffering, waiting: buffering, loadeddata: ready, canplay: ready, playing: ready, error: ready }
    for (const [type, listener] of Object.entries(listeners)) video.addEventListener(type, listener)
    onCleanup(() => {
      for (const [type, listener] of Object.entries(listeners)) video.removeEventListener(type, listener)
    })
  }, { immediate: true })

  function startLoad() {
    if (hls === null || loading || !toValue(load)) return
    loading = true
    hls.startLoad()
  }

  watch(
    [element, () => toValue(source)?.src, () => toValue(source)?.hls, () => failedStreams.size],
    ([video, src, stream], _previous, onCleanup) => {
      if (video === null || src === undefined) return
      if (!stream || failedStreams.has(stream) || video.canPlayType(HLS_MIME) !== '') {
        const next = stream && !failedStreams.has(stream) ? stream : src
        if (video.getAttribute('src') !== next) video.src = next
        return
      }

      let disposed = false
      onCleanup(() => {
        disposed = true
        hls?.destroy()
        hls = null
        loading = false
      })
      attaching = import('hls.js/light')
        .then(({ default: Hls }) => {
          if (disposed) return
          if (!Hls.isSupported()) {
            video.src = src
            return
          }
          hls = new Hls({ autoStartLoad: false, capLevelToPlayerSize: true })
          hls.on(Hls.Events.ERROR, (_event, data) => {
            if (data.fatal) failedStreams.add(stream)
          })
          hls.loadSource(stream)
          hls.attachMedia(video)
          startLoad()
        })
        .catch(() => {
          if (!disposed) failedStreams.add(stream)
        })
    },
    { immediate: true },
  )

  watch(() => toValue(load), startLoad)

  /**
   * Call from the `<video>` error handler: true when the failed source was the
   * stream and the MP4 is now playing instead, false when the video itself is broken.
   */
  function recover(): boolean {
    const stream = toValue(source)?.hls
    if (!stream || failedStreams.has(stream)) return false
    failedStreams.add(stream)
    return true
  }

  async function play(): Promise<void> {
    await attaching
    await element.value?.play()
  }

  return { play, recover, isBuffering: readonly(isBuffering) }
}
